import { useState } from 'react';
import { useQueryState } from 'nuqs';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import { isSafeOAuthRedirectUri } from '@reflex/shared';

interface PickerOrganization {
  id: string;
  slug: string;
  name: string;
  avatarUrl: string | null;
}

interface PickerResponse {
  organizations: PickerOrganization[];
  /** Friendly name of the MCP client the user will return to, when known. */
  clientName: string | null;
}

interface PickerErrorBody {
  error?: string;
  error_description?: string;
}

async function fetchPickerOrganizations(selection: string): Promise<PickerResponse> {
  const res = await fetch(
    `/api/oauth/select-organization?selection=${encodeURIComponent(selection)}`,
    { headers: { Accept: 'application/json' } },
  );
  const body = (await res.json().catch(() => ({}))) as {
    organizations?: PickerOrganization[];
    client_name?: string | null;
  } & PickerErrorBody;
  if (!res.ok) {
    throw new Error(
      body.error_description ?? body.error ?? `Failed to load organizations (${res.status})`,
    );
  }
  return { organizations: body.organizations ?? [], clientName: body.client_name ?? null };
}

async function submitOrganizationChoice(
  selection: string,
  organizationId: string,
): Promise<{ redirect_uri: string }> {
  const res = await fetch('/api/oauth/select-organization', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ selection, organization_id: organizationId }),
  });
  const body = (await res.json().catch(() => ({}))) as { redirect_uri?: string } & PickerErrorBody;
  if (!res.ok || !body.redirect_uri) {
    throw new Error(
      body.error_description ?? body.error ?? `Failed to finish sign-in (${res.status})`,
    );
  }
  return { redirect_uri: body.redirect_uri };
}

/**
 * Public landing page for the MCP OAuth org-selection step.
 *
 * When an MCP client (Claude, Cursor, …) drives a user through the OAuth
 * login and the user belongs to more than one organization, the API
 * server's callback hook parks the in-flight auth code and redirects the
 * browser here with a one-time `selection` id. This page fetches the
 * user's memberships via `GET /api/oauth/select-organization?selection=`,
 * lets the user pick one, and POSTs the choice back. The server validates
 * membership, mints the auth code bound to the chosen org, and returns
 * the OAuth client's `redirect_uri` (with `code` + `state` appended) so
 * we can hand control back to the client.
 *
 * The page is not gated by `AuthGate` — at this point the user has
 * completed provider auth but does not yet hold a web session, and the
 * `selection` id is the only auth the server needs.
 */
export function SelectOrganizationPage() {
  const [selection] = useQueryState('selection');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittingOrgId, setSubmittingOrgId] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const query = useQuery({
    queryKey: ['oauth-select-organization', selection],
    queryFn: () => fetchPickerOrganizations(selection ?? ''),
    enabled: !!selection,
    retry: false,
  });

  const handlePick = async (organizationId: string) => {
    if (!selection || submitting) return;
    setSubmitting(true);
    setSubmittingOrgId(organizationId);
    setSubmitError(null);
    try {
      const { redirect_uri } = await submitOrganizationChoice(selection, organizationId);
      // The server refuses unsafe redirect URIs too, but this page performs the
      // navigation, so it checks what it is about to follow: `location.assign`
      // on a `javascript:` target runs it in this document, on the app origin.
      // The other refused schemes are defense in depth.
      if (!isSafeOAuthRedirectUri(redirect_uri)) {
        throw new Error(
          'Your MCP client sent a return address this page won’t open. Restart the connection from your client, and contact its publisher if this keeps happening.',
        );
      }
      // The choice succeeded and the auth code is minted. Surface a success
      // state before handing control back: when the client's `redirect_uri`
      // is a native-app scheme (e.g. `cursor://`) the browser tab stays open
      // after the OS hands off, so the user needs an explicit confirmation
      // that they can return to the application.
      setCompleted(true);
      // Hand control back to the OAuth client (e.g. Claude). The redirect
      // target is the client-supplied `redirect_uri` with the freshly
      // minted auth code + the original `state`; we navigate the top-level
      // window so the client owns the next step.
      window.location.assign(redirect_uri);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to finish sign-in';
      setSubmitError(message);
      setSubmitting(false);
      setSubmittingOrgId(null);
    }
  };

  const organizations = query.data?.organizations ?? [];
  const clientName = query.data?.clientName ?? null;
  const clientLabel = clientName ?? 'your MCP client';
  const loadError = query.error instanceof Error ? query.error.message : null;
  const showLoading = !!selection && query.isLoading;

  return (
    <div
      className="flex min-h-dvh items-center justify-center bg-background p-4"
      data-testid="oauth-select-organization-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <PanelTitle>{completed ? 'You’re all set' : 'Choose an organization'}</PanelTitle>
          <CardDescription>
            {completed
              ? 'Sign-in is complete and this session is now scoped to your organization.'
              : `You signed in from ${
                  clientName ? clientName : 'an MCP client (Claude, Cursor, …)'
                } and belong to more than one organization. Pick the one this session should use: every request from ${
                  clientName ? clientName : 'the client'
                } will be scoped to it. You can reconnect later to switch.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {completed && (
            <Alert data-testid="oauth-select-organization-success">
              <CheckCircle2 className="size-4" />
              <AlertTitle>Authentication complete</AlertTitle>
              <AlertDescription>
                You can return to {clientLabel}. It should continue automatically. It’s safe to
                close this tab.
              </AlertDescription>
            </Alert>
          )}

          {!completed && !selection && (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>Missing selection</AlertTitle>
              <AlertDescription>
                This link is missing a selection id. Restart the MCP connection from your client to
                try again.
              </AlertDescription>
            </Alert>
          )}

          {!completed && showLoading && (
            <p className="text-center text-sm text-muted-foreground">Loading organizations...</p>
          )}

          {!completed && selection && loadError && (
            <Alert variant="destructive" data-testid="oauth-select-organization-load-error">
              <AlertCircle className="size-4" />
              <AlertTitle>Sign-in link expired</AlertTitle>
              <AlertDescription>
                {loadError} Return to your MCP client (Claude, Cursor, …) and restart the connection
                to try again.
              </AlertDescription>
            </Alert>
          )}

          {!completed && selection && !loadError && !showLoading && organizations.length === 0 && (
            <p className="text-center text-sm text-muted-foreground">
              You are not a member of any organization yet.
            </p>
          )}

          {!completed && organizations.length > 0 && (
            <ul className="flex flex-col gap-2">
              {organizations.map((org) => (
                <li key={org.id}>
                  <Button
                    variant="outline"
                    className="h-auto w-full justify-start gap-3 px-3 py-3"
                    data-testid={`oauth-select-organization-option-${org.id}`}
                    onClick={() => void handlePick(org.id)}
                    disabled={submitting}
                  >
                    {org.avatarUrl ? (
                      <img
                        src={org.avatarUrl}
                        alt=""
                        className="size-8 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground">
                        {org.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                      <span className="truncate text-sm font-medium">{org.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{org.slug}</span>
                    </div>
                    {submittingOrgId === org.id && (
                      <span className="text-xs text-muted-foreground">Connecting...</span>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {submitError && (
            <Alert variant="destructive" data-testid="oauth-select-organization-submit-error">
              <AlertCircle className="size-4" />
              <AlertTitle>Could not finish sign-in</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
